import {
  GATEWAY_ERROR_KINDS,
  ResponderGatewayError,
} from '@/shared-responder-v2';
import type { ScreenerFlowEvent } from '@/shared-responder-v2-core';
import { getPanelRedirectUrl, type ResponderPanel } from '@/shared-survey';
import { ScreenerSessionStatus } from '@/survey-graphql';

import type { AnswerScreenerQuestionResult } from './shared-responder-v2-gateway-screener-gateway';

const ABSOLUTE_REDIRECT_URL = /^https?:\/\/\S/i;
const ROOT_RELATIVE_REDIRECT_URL = /^\/[^/]/;

/**
 * Only an http(s) or root-relative target is a navigable redirect. A blank
 * value would make `location.replace` reload the screener (disqualify, reload,
 * disqualify…), and a `javascript:`/`data:` target would execute on our origin —
 * both resolve to no redirect, which shows the terminal card instead.
 */
export function resolveScreenerRedirectUrl(
  value: string | null
): string | null {
  if (value === null) {
    return null;
  }

  const trimmed = value.trim();

  return ABSOLUTE_REDIRECT_URL.test(trimmed) ||
    ROOT_RELATIVE_REDIRECT_URL.test(trimmed)
    ? trimmed
    : null;
}

export function resolveDisqualifiedRedirectUrl(
  panel: ResponderPanel | null,
  redirectUrl: string | null
): string | null {
  return (panel && getPanelRedirectUrl('TERMINATE', panel)) ?? redirectUrl;
}

export function resolveScreenerEventRedirectUrl(
  panel: ResponderPanel | null,
  event: ScreenerFlowEvent
): string | null {
  if (event.type !== 'disqualified') return null;
  return resolveDisqualifiedRedirectUrl(panel, event.redirectUrl ?? null);
}

/**
 * An `active` result without a next question and a `completed` result without
 * a key are both protocol violations, not renderable states — they fail rather
 * than strand the respondent on a blank card.
 */
export function toScreenerAnswerEvent(
  result: AnswerScreenerQuestionResult
): ScreenerFlowEvent {
  if (result.status === ScreenerSessionStatus.Disqualified) {
    return {
      type: 'disqualified',
      redirectUrl: resolveScreenerRedirectUrl(
        result.disqualificationRedirectUrl
      ),
    };
  }

  if (result.status === ScreenerSessionStatus.Completed) {
    return result.projectSessionKey === null
      ? { type: 'failed' }
      : { type: 'completed', sessionKey: result.projectSessionKey };
  }

  return result.question === null
    ? { type: 'failed' }
    : { type: 'nextQuestion', question: result.question };
}

/**
 * `restarted` covers both a session past its TTL and one already spent: the
 * only recovery for either is a fresh session. An invalid answer keeps the
 * respondent on the current question — the UI constrains answers to option
 * values, so this only fires on a race, and resubmitting is the right retry.
 */
export function toScreenerAnswerFailureEvent(
  error: unknown
): ScreenerFlowEvent {
  if (!(error instanceof ResponderGatewayError)) {
    return { type: 'failed' };
  }

  switch (error.kind) {
    case GATEWAY_ERROR_KINDS.ScreenerSessionNotFound:
    case GATEWAY_ERROR_KINDS.ScreenerSessionCompleted:
      return { type: 'restarted' };
    case GATEWAY_ERROR_KINDS.ScreenerAnswerInvalid:
      return { type: 'answerRejected' };
    case GATEWAY_ERROR_KINDS.ScreenerSessionDisqualified:
      return { type: 'disqualified', redirectUrl: null };
    default:
      return { type: 'failed' };
  }
}
